import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import Tooltip from "../Tooltip";
import { useInView } from "../../hooks/useInView";
import { StripeBillingCompactMessage } from "../StripeBillingGate";

// Security constants
const SUBMISSION_COOLDOWN = 1000; // 1 second between API calls

// Validate URL parameters
const validateStudioId = (studioId: string | null): boolean => {
  if (!studioId || typeof studioId !== 'string') return false;
  // UUID format validation
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(studioId);
};

// Security event logging
const logSecurityEvent = (event: string, details: any) => {
  console.warn(`Security Event: ${event}`, {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    details
  });
};

type MrrBreakdown = {
  stripe: { mrr: number; studentCount: number };
  manual: { mrr: number; studentCount: number };
  total: { mrr: number };
};

type Metrics = {
  mrr: number;
  breakdown?: MrrBreakdown;
};

export default function AdditionalMetricsCard({
  studioId,
  isStripeConnected,
  canUseStripeBilling = true,
  retentionRate,
  churnRate,
}: {
  studioId: string | null;
  isStripeConnected: boolean | null;
  canUseStripeBilling?: boolean;
  retentionRate: number;
  churnRate: number;
}) {
  const { getAuthToken } = useAuth();
  const { ref, inView } = useInView();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);

  useEffect(() => {
    if (!canUseStripeBilling || isStripeConnected === false) {
      setLoading(false);
    }
  }, [canUseStripeBilling, isStripeConnected]);

  useEffect(() => {
    const run = async () => {
      if (!inView || !studioId || !canUseStripeBilling || !isStripeConnected || metrics) return;
      
      // Security validations
      if (!validateStudioId(studioId)) {
        logSecurityEvent('INVALID_STUDIO_ID', { studioId });
        return;
      }
      
      // Rate limiting
      const now = Date.now();
      if (now - lastFetchTime < SUBMISSION_COOLDOWN) {
        return;
      }
      
      const token = getAuthToken();
      if (!token) return;
      
      setLoading(true);
      setLastFetchTime(now);
      
      try {
        // Use encodeURIComponent for URL parameters
        const encodedStudioId = encodeURIComponent(studioId);
        
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/stripe/metrics/${encodedStudioId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (res.ok) {
          const json = await res.json();
          // Validate and sanitize numeric values
          const mrr = typeof json.mrr === 'number' && json.mrr >= 0 ? json.mrr : 0;
          
          setMetrics({
            mrr,
            breakdown: json.breakdown || undefined,
          });
        } else {
          logSecurityEvent('API_REQUEST_FAILED', { 
            status: res.status, 
            statusText: res.statusText,
            endpoint: 'stripe/metrics'
          });
        }
      } catch (error) {
        logSecurityEvent('API_REQUEST_ERROR', { 
          error: error instanceof Error ? error.message : 'Unknown error',
          endpoint: 'stripe/metrics'
        });
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [inView, studioId, canUseStripeBilling, isStripeConnected, getAuthToken, metrics, lastFetchTime]);

  return (
    <div ref={ref} className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-800 px-4 py-5 border-2 border-blue-100 dark:border-blue-800 shadow-md rounded-xl sm:px-6 sm:py-6 hover:shadow-lg hover:-translate-y-1 transition">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Additional Metrics</h3>
      {(!inView || loading) && canUseStripeBilling && isStripeConnected && !metrics ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-pulse flex space-x-4 items-center">
            <div className="h-5 w-5 bg-indigo-200 dark:bg-indigo-600 rounded-full animate-spin"></div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Loading metrics...</div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {!canUseStripeBilling || isStripeConnected !== true ? (
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">Monthly Recurring Revenue</span>
              <StripeBillingCompactMessage
                canUseStripeBilling={canUseStripeBilling}
                isStripeConnected={isStripeConnected}
              />
            </div>
          ) : (
            <div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Monthly Recurring Revenue</span>
                <span className="font-semibold text-gray-900 dark:text-white">${(metrics?.mrr ?? 0).toLocaleString()}</span>
              </div>
              
              {/* MRR Breakdown */}
              {metrics?.breakdown && (metrics.breakdown.stripe.studentCount > 0 || metrics.breakdown.manual.studentCount > 0) && (
                <div className="mt-2 space-y-1.5 pl-4">
                  {metrics.breakdown.stripe.studentCount > 0 && (
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5">
                        <span>💳</span>
                        <span className="text-gray-600 dark:text-gray-400">Auto Billing</span>
                      </div>
                      <span className="font-medium text-blue-600 dark:text-blue-400">
                        ${metrics.breakdown.stripe.mrr.toLocaleString()}
                      </span>
                    </div>
                  )}
                  
                  {metrics.breakdown.manual.studentCount > 0 && (
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5">
                        <span>💵</span>
                        <span className="text-gray-600 dark:text-gray-400">Cash/Manual</span>
                      </div>
                      <span className="font-medium text-green-600 dark:text-green-400">
                        ${metrics.breakdown.manual.mrr.toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <Tooltip text="(Active Students / Total Students) × 100">
                <span className="text-gray-600 dark:text-gray-400">Student Retention Rate</span>
              </Tooltip>
            </div>
            <span className="font-semibold text-green-600 dark:text-green-400 select-none">{retentionRate.toFixed(1)}%</span>
          </div>
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <Tooltip text="(Inactive Students / Total Students) × 100">
                <span className="text-gray-600 dark:text-gray-400">Churn Rate</span>
              </Tooltip>
            </div>
            <span className="font-semibold text-red-600 dark:text-red-400 select-none">{churnRate.toFixed(1)}%</span>
          </div>
        </div>
      )}
    </div>
  );
}

